import { create } from '@bufbuild/protobuf'

import {
  WorkflowActionType,
  WorkflowBranchNodeConfigSchema,
  WorkflowCreateTaskNodeConfigSchema,
  WorkflowEdgeSchema,
  WorkflowNodePositionSchema,
  WorkflowNodeSchema,
  WorkflowNodeType,
  WorkflowSendTemplatedEmailNodeConfigSchema,
  WorkflowSendWebhookNodeConfigSchema,
  WorkflowSwitchCaseSchema,
  WorkflowSwitchNodeConfigSchema,
  WorkflowUpdateFieldNodeConfigSchema,
  WorkflowWaitNodeConfigSchema,
  type WorkflowAction,
  type WorkflowBranchNodeConfig,
  type WorkflowCondition,
  type WorkflowCreateTaskNodeConfig,
  type WorkflowEdge,
  type WorkflowNode,
  type WorkflowSendTemplatedEmailNodeConfig,
  type WorkflowSendWebhookNodeConfig,
  type WorkflowSwitchNodeConfig,
  type WorkflowTriggerEntityType,
  type WorkflowUpdateFieldNodeConfig,
  type WorkflowWaitNodeConfig,
} from '@/gen/cortado/v1/cortado_pb'

import {
  deserializeActionConfig,
  legacyDelayFromActionConfig,
  serializeWaitDuration,
} from '../action-config'

/**
 * Branch edge label. Only edges leaving a BRANCH node carry a label; it is
 * always one of these two literals.
 */
export type BranchLabel = 'true' | 'false'
export type OutputLabel = string

/**
 * TypedConfig mirrors the proto WorkflowNode.config oneof in a discriminated
 * union keyed by `case`. The `case: undefined` member covers nodes without a
 * config payload (TRIGGER, STOP). Each value field is a plain object that maps
 * 1:1 to the matching proto config message (sans `$typeName`), so it survives a
 * builder -> proto -> builder round-trip unchanged.
 */
export type TypedConfig =
  | { case: 'wait'; value: WaitNodeConfigData }
  | { case: 'sendTemplatedEmail'; value: SendTemplatedEmailNodeConfigData }
  | { case: 'createTask'; value: CreateTaskNodeConfigData }
  | { case: 'updateField'; value: UpdateFieldNodeConfigData }
  | { case: 'sendWebhook'; value: SendWebhookNodeConfigData }
  | { case: 'branch'; value: BranchNodeConfigData }
  | { case: 'switch'; value: SwitchNodeConfigData }
  | { case: undefined; value?: undefined }

export type WaitNodeConfigData = {
  duration: string
}

export type SendTemplatedEmailNodeConfigData = {
  toField: string
  toEmail: string
  cc: string[]
  bcc: string[]
  subjectTemplate: string
  bodyTemplateHtml: string
  templateName: string
  templateId: string
}

export type CreateTaskNodeConfigData = {
  title: string
  description: string
  dueInDays: number
  assignedToUserId: string
  assignedToStrategy: string
}

export type UpdateFieldNodeConfigData = {
  field: string
  value: string
}

export type SendWebhookNodeConfigData = {
  /** workflows.webhook_endpoints.id — the registered endpoint to deliver to. */
  endpointId: string
}

export type BranchNodeConfigData = {
  // The proto condition is carried as-is; the branch editor builds it through
  // the existing condition-serialization helpers.
  condition?: WorkflowCondition
}

export type SwitchCaseData = {
  id: string
  label: string
  values: string[]
}

export type SwitchNodeConfigData = {
  field: string
  targetEntityType?: WorkflowTriggerEntityType
  cases: SwitchCaseData[]
}

export type BuilderNode = {
  id: string
  type: WorkflowNodeType
  label?: string
  /** Plain-English note describing what this step does. Display-only. */
  description?: string
  position: { x: number; y: number }
  config: TypedConfig
}

export type BuilderEdge = {
  id: string
  from: string
  to: string
  label?: OutputLabel
}

export type BuilderGraph = {
  nodes: BuilderNode[]
  edges: BuilderEdge[]
}

// ---------------------------------------------------------------------------
// proto -> builder
// ---------------------------------------------------------------------------

export function nodeFromProto(node: WorkflowNode): BuilderNode {
  return {
    id: node.id,
    type: node.nodeType,
    label: node.label || undefined,
    description: node.description || undefined,
    position: { x: node.position?.x ?? 0, y: node.position?.y ?? 0 },
    config: configFromProto(node),
  }
}

export function edgeFromProto(edge: WorkflowEdge): BuilderEdge {
  return {
    id: edge.id,
    from: edge.fromNodeId,
    to: edge.toNodeId,
    label: outputLabelFromString(edge.branchLabel),
  }
}

export function graphFromProto(nodes: WorkflowNode[], edges: WorkflowEdge[]): BuilderGraph {
  return {
    nodes: nodes.map(nodeFromProto),
    edges: edges.map(edgeFromProto),
  }
}

function configFromProto(node: WorkflowNode): TypedConfig {
  switch (node.config.case) {
    case 'wait':
      return { case: 'wait', value: { duration: node.config.value.duration } }
    case 'sendTemplatedEmail': {
      const v = node.config.value
      return {
        case: 'sendTemplatedEmail',
        value: {
          toField: v.toField,
          toEmail: v.toEmail,
          cc: [...v.cc],
          bcc: [...v.bcc],
          subjectTemplate: v.subjectTemplate,
          bodyTemplateHtml: v.bodyTemplateHtml,
          templateName: v.templateName,
          templateId: v.templateId,
        },
      }
    }
    case 'createTask': {
      const v = node.config.value
      return {
        case: 'createTask',
        value: {
          title: v.title,
          description: v.description,
          dueInDays: v.dueInDays,
          assignedToUserId: v.assignedToUserId,
          assignedToStrategy: v.assignedToStrategy,
        },
      }
    }
    case 'updateField': {
      const v = node.config.value
      return { case: 'updateField', value: { field: v.field, value: v.value } }
    }
    case 'sendWebhook':
      return { case: 'sendWebhook', value: { endpointId: node.config.value.endpointId } }
    case 'branch':
      return {
        case: 'branch',
        value: { condition: node.config.value.condition },
      }
    case 'switch': {
      const v = node.config.value
      return {
        case: 'switch',
        value: {
          field: v.field,
          targetEntityType: v.targetEntityType,
          cases: v.cases.map((switchCase) => ({
            id: switchCase.id,
            label: switchCase.label,
            values: [...switchCase.values],
          })),
        },
      }
    }
    default:
      return { case: undefined }
  }
}

// ---------------------------------------------------------------------------
// builder -> proto
// ---------------------------------------------------------------------------

export function nodeToProto(node: BuilderNode): WorkflowNode {
  return create(WorkflowNodeSchema, {
    id: node.id,
    nodeType: node.type,
    label: node.label ?? '',
    description: node.description ?? '',
    position: create(WorkflowNodePositionSchema, {
      x: node.position.x,
      y: node.position.y,
    }),
    config: configToProto(node.config),
  })
}

export function edgeToProto(edge: BuilderEdge): WorkflowEdge {
  return create(WorkflowEdgeSchema, {
    id: edge.id,
    fromNodeId: edge.from,
    toNodeId: edge.to,
    branchLabel: edge.label ?? '',
  })
}

function configToProto(config: TypedConfig): WorkflowNode['config'] {
  switch (config.case) {
    case 'wait':
      return {
        case: 'wait',
        value: create(WorkflowWaitNodeConfigSchema, {
          duration: config.value.duration,
        }) satisfies WorkflowWaitNodeConfig,
      }
    case 'sendTemplatedEmail':
      return {
        case: 'sendTemplatedEmail',
        value: create(WorkflowSendTemplatedEmailNodeConfigSchema, {
          toField: config.value.toField,
          toEmail: config.value.toEmail,
          cc: config.value.cc,
          bcc: config.value.bcc,
          subjectTemplate: config.value.subjectTemplate,
          bodyTemplateHtml: config.value.bodyTemplateHtml,
          templateName: config.value.templateName,
          templateId: config.value.templateId,
        }) satisfies WorkflowSendTemplatedEmailNodeConfig,
      }
    case 'createTask':
      return {
        case: 'createTask',
        value: create(WorkflowCreateTaskNodeConfigSchema, {
          title: config.value.title,
          description: config.value.description,
          dueInDays: config.value.dueInDays,
          assignedToUserId: config.value.assignedToUserId,
          assignedToStrategy: config.value.assignedToStrategy,
        }) satisfies WorkflowCreateTaskNodeConfig,
      }
    case 'updateField':
      return {
        case: 'updateField',
        value: create(WorkflowUpdateFieldNodeConfigSchema, {
          field: config.value.field,
          value: config.value.value,
        }) satisfies WorkflowUpdateFieldNodeConfig,
      }
    case 'sendWebhook':
      return {
        case: 'sendWebhook',
        value: create(WorkflowSendWebhookNodeConfigSchema, {
          endpointId: config.value.endpointId,
        }) satisfies WorkflowSendWebhookNodeConfig,
      }
    case 'branch':
      return {
        case: 'branch',
        value: create(WorkflowBranchNodeConfigSchema, {
          condition: config.value.condition,
        }) satisfies WorkflowBranchNodeConfig,
      }
    case 'switch':
      return {
        case: 'switch',
        value: create(WorkflowSwitchNodeConfigSchema, {
          field: config.value.field,
          targetEntityType: config.value.targetEntityType,
          cases: config.value.cases.map((switchCase) =>
            create(WorkflowSwitchCaseSchema, switchCase)
          ),
        }) satisfies WorkflowSwitchNodeConfig,
      }
    default:
      return { case: undefined }
  }
}

export function graphToProto(graph: BuilderGraph): {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
} {
  return {
    nodes: graph.nodes.map(nodeToProto),
    edges: graph.edges.map(edgeToProto),
  }
}

function outputLabelFromString(value: string): OutputLabel | undefined {
  return value || undefined
}

// ---------------------------------------------------------------------------
// graph queries
// ---------------------------------------------------------------------------

export function findTrigger(graph: BuilderGraph): BuilderNode | undefined {
  return graph.nodes.find((node) => node.type === WorkflowNodeType.TRIGGER)
}

export function nodeById(graph: BuilderGraph, id: string): BuilderNode | undefined {
  return graph.nodes.find((node) => node.id === id)
}

export function outgoingEdges(graph: BuilderGraph, nodeId: string): BuilderEdge[] {
  return graph.edges.filter((edge) => edge.from === nodeId)
}

export function incomingEdges(graph: BuilderGraph, nodeId: string): BuilderEdge[] {
  return graph.edges.filter((edge) => edge.to === nodeId)
}

/**
 * topoOrder returns node ids in a stable breadth-first order starting from the
 * trigger, following edges (branch true edge before false edge). Nodes
 * unreachable from the trigger are appended at the end in declaration order so
 * the form-mode list never silently hides a node. Cycles are tolerated (each
 * node is emitted once).
 */
export function topoOrder(graph: BuilderGraph): string[] {
  const trigger = findTrigger(graph)
  const visited = new Set<string>()
  const order: string[] = []

  const walk = (startId: string) => {
    // Head-index BFS (not Array.shift, which is O(n) per dequeue).
    const queue: string[] = [startId]
    for (let head = 0; head < queue.length; head++) {
      const id = queue[head]
      if (visited.has(id)) continue
      const node = nodeById(graph, id)
      if (!node) continue
      visited.add(id)
      order.push(id)

      const outs = sortRoutingEdges(node, outgoingEdges(graph, id))
      for (const edge of outs) {
        if (!visited.has(edge.to)) queue.push(edge.to)
      }
    }
  }

  if (trigger) walk(trigger.id)

  // Append any disconnected nodes (declaration order) so they stay visible.
  for (const node of graph.nodes) {
    if (!visited.has(node.id)) {
      visited.add(node.id)
      order.push(node.id)
    }
  }

  return order
}

export function topoNodes(graph: BuilderGraph): BuilderNode[] {
  const order = topoOrder(graph)
  const index = new Map(order.map((id, i) => [id, i]))
  return [...graph.nodes].sort((a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0))
}

// Keep named routing outputs in their configured order for deterministic graph
// traversal: True/False for Branch, ordered cases/Otherwise for Switch.
function sortRoutingEdges(node: BuilderNode, edges: BuilderEdge[]): BuilderEdge[] {
  const labels = routingOutputLabels(node)
  const rank = (label?: OutputLabel) => {
    const index = label ? labels.indexOf(label) : -1
    return index >= 0 ? index : labels.length
  }
  return [...edges].sort((a, b) => rank(a.label) - rank(b.label))
}

export function routingOutputLabels(node: BuilderNode): OutputLabel[] {
  if (node.type === WorkflowNodeType.BRANCH) return ['true', 'false']
  if (node.type === WorkflowNodeType.SWITCH && node.config.case === 'switch') {
    return [...node.config.value.cases.map((switchCase) => switchCase.id), 'otherwise']
  }
  return []
}

// ---------------------------------------------------------------------------
// structural mutations (pure: return a new graph)
// ---------------------------------------------------------------------------

const INSERT_X_OFFSET = 220

/**
 * insertNodeOnEdge splits an existing edge by placing a new node between its
 * endpoints. The upstream segment keeps the original edge's branch label (so a
 * node inserted on a branch's true/false edge preserves which side it's on);
 * the downstream segment is unlabeled. The new node is positioned near the
 * midpoint of the split edge.
 *
 * For BRANCH insertions the wiring differs: upstream -> branch,
 * branch --false--> former-downstream, branch --true--> new STOP node placed to
 * the side. Returns the new graph plus the id of the inserted node.
 */
export function insertNodeOnEdge(
  graph: BuilderGraph,
  edgeId: string,
  type: WorkflowNodeType
): { graph: BuilderGraph; insertedId: string } {
  const edge = graph.edges.find((e) => e.id === edgeId)
  if (!edge) return { graph, insertedId: '' }

  // STOP is terminal: inserting it mid-edge would give it an outgoing edge,
  // producing an invalid graph. The palette already hides STOP for edge targets;
  // this is the structural backstop so the helper can never emit that shape.
  if (type === WorkflowNodeType.STOP) return { graph, insertedId: '' }

  const fromNode = nodeById(graph, edge.from)
  const toNode = nodeById(graph, edge.to)
  const midpoint = {
    x: ((fromNode?.position.x ?? 0) + (toNode?.position.x ?? 0)) / 2,
    y: ((fromNode?.position.y ?? 0) + (toNode?.position.y ?? 0)) / 2,
  }

  const inserted = makeNode(type, midpoint)
  const remainingEdges = graph.edges.filter((e) => e.id !== edgeId)

  if (isRoutingNodeType(type)) {
    const stop = makeNode(WorkflowNodeType.STOP, {
      x: midpoint.x + INSERT_X_OFFSET,
      y: midpoint.y,
    })
    return {
      graph: {
        nodes: [...graph.nodes, inserted, stop],
        edges: [
          ...remainingEdges,
          makeEdge(edge.from, inserted.id, edge.label),
          makeEdge(inserted.id, edge.to, fallbackOutputLabel(inserted)),
          makeEdge(inserted.id, stop.id, primaryOutputLabel(inserted)),
        ],
      },
      insertedId: inserted.id,
    }
  }

  return {
    graph: {
      nodes: [...graph.nodes, inserted],
      edges: [
        ...remainingEdges,
        makeEdge(edge.from, inserted.id, edge.label),
        makeEdge(inserted.id, edge.to),
      ],
    },
    insertedId: inserted.id,
  }
}

/**
 * appendNodeAfter adds a new node downstream of a leaf node, or on one explicit
 * missing true/false output of a BRANCH. A BRANCH appended this way also gets a
 * STOP on its own true edge. Returns the new graph plus the inserted node id.
 */
export function appendNodeAfter(
  graph: BuilderGraph,
  nodeId: string,
  type: WorkflowNodeType,
  outputLabel?: OutputLabel
): { graph: BuilderGraph; insertedId: string } {
  const anchor = nodeById(graph, nodeId)
  if (!anchor) return { graph, insertedId: '' }
  if (!canAppendFromSource(graph, anchor, outputLabel)) {
    return { graph, insertedId: '' }
  }

  const position = {
    x: anchor.position.x,
    y: anchor.position.y + VERTICAL_GAP,
  }
  const inserted = makeNode(type, position)

  if (isRoutingNodeType(type)) {
    const stop = makeNode(WorkflowNodeType.STOP, {
      x: position.x + INSERT_X_OFFSET,
      y: position.y,
    })
    return {
      graph: {
        nodes: [...graph.nodes, inserted, stop],
        edges: [
          ...graph.edges,
          makeEdge(nodeId, inserted.id, outputLabel),
          makeEdge(inserted.id, stop.id, primaryOutputLabel(inserted)),
        ],
      },
      insertedId: inserted.id,
    }
  }

  return {
    graph: {
      nodes: [...graph.nodes, inserted],
      edges: [...graph.edges, makeEdge(nodeId, inserted.id, outputLabel)],
    },
    insertedId: inserted.id,
  }
}

/**
 * appendNodeAtPosition is appendNodeAfter's free-placement sibling: it appends a
 * new node downstream of `sourceNodeId` but drops it at the caller-supplied
 * `position` (flow coords) instead of one VERTICAL_GAP below the source. This
 * backs the Attio-style connector drag-off, where the user releases the wire at
 * an arbitrary canvas point and the node should stay exactly there rather than
 * being re-snapped under its parent. Like appendNodeAfter, a BRANCH appended this
 * way also gets a STOP on its true edge, placed INSERT_X_OFFSET to the right of
 * the drop point so the two arms don't overlap. Returns the new graph plus the
 * inserted node id (unchanged with insertedId '' if the source node is missing).
 */
export function appendNodeAtPosition(
  graph: BuilderGraph,
  sourceNodeId: string,
  type: WorkflowNodeType,
  position: { x: number; y: number },
  outputLabel?: OutputLabel
): { graph: BuilderGraph; insertedId: string } {
  const source = nodeById(graph, sourceNodeId)
  if (!source) return { graph, insertedId: '' }
  if (!canAppendFromSource(graph, source, outputLabel)) {
    return { graph, insertedId: '' }
  }

  const inserted = makeNode(type, position)

  if (isRoutingNodeType(type)) {
    const stop = makeNode(WorkflowNodeType.STOP, {
      x: position.x + INSERT_X_OFFSET,
      y: position.y,
    })
    return {
      graph: {
        nodes: [...graph.nodes, inserted, stop],
        edges: [
          ...graph.edges,
          makeEdge(sourceNodeId, inserted.id, outputLabel),
          makeEdge(inserted.id, stop.id, primaryOutputLabel(inserted)),
        ],
      },
      insertedId: inserted.id,
    }
  }

  return {
    graph: {
      nodes: [...graph.nodes, inserted],
      edges: [...graph.edges, makeEdge(sourceNodeId, inserted.id, outputLabel)],
    },
    insertedId: inserted.id,
  }
}

/**
 * canConnect reports whether a direct edge sourceId -> targetId may be created by
 * the connector drag-off interaction. It rejects connections that would corrupt
 * the graph's invariants:
 *
 *  - a node connecting to itself;
 *  - either endpoint missing from the graph;
 *  - targeting the TRIGGER (the trigger has no inbound edge — it is the root);
 *  - dragging off a STOP node (terminal nodes have no outbound edge);
 *  - a duplicate of an existing edge (same from/to);
 *  - a non-branch source that already has an outgoing edge;
 *  - a BRANCH source without an explicit true/false output, or whose requested
 *    output is already connected.
 *
 * Otherwise the connection is allowed.
 */
export function canConnect(
  graph: BuilderGraph,
  sourceId: string,
  targetId: string,
  outputLabel?: OutputLabel
): boolean {
  if (sourceId === targetId) return false

  const source = nodeById(graph, sourceId)
  const target = nodeById(graph, targetId)
  if (!source || !target) return false

  if (target.type === WorkflowNodeType.TRIGGER) return false
  if (source.type === WorkflowNodeType.STOP) return false

  const duplicate = graph.edges.some((edge) => edge.from === sourceId && edge.to === targetId)
  if (duplicate) return false

  const outgoing = outgoingEdges(graph, sourceId)
  if (isRoutingNode(source)) {
    if (!outputLabel || !routingOutputLabels(source).includes(outputLabel)) return false
    if (outgoing.some((edge) => edge.label === outputLabel)) return false
  } else if (outgoing.length > 0) {
    return false
  }

  return true
}

/**
 * connectNodes appends a new edge sourceId -> targetId, preserving the selected
 * true/false output for a BRANCH source. It assumes the caller has already
 * validated the pair with canConnect (it performs no checks of its own) and,
 * like the other structural helpers, returns a new graph without mutating input.
 */
export function connectNodes(
  graph: BuilderGraph,
  sourceId: string,
  targetId: string,
  outputLabel?: OutputLabel
): BuilderGraph {
  return {
    nodes: graph.nodes,
    edges: [...graph.edges, makeEdge(sourceId, targetId, outputLabel)],
  }
}

function canAppendFromSource(
  graph: BuilderGraph,
  source: BuilderNode,
  outputLabel?: OutputLabel
): boolean {
  if (!isRoutingNode(source)) return outputLabel === undefined
  if (!outputLabel || !routingOutputLabels(source).includes(outputLabel)) return false
  const outgoing = outgoingEdges(graph, source.id)
  return !outgoing.some((edge) => edge.label === outputLabel)
}

/**
 * duplicateNode clones an existing node (never the TRIGGER) as a sibling. The
 * copy gets a fresh id, a deep copy of the source config, and is offset down and
 * to the right of the source. Wiring is intentionally minimal: if the source has
 * exactly one incoming edge, the duplicate is attached to that same parent with
 * a new edge carrying the same branch label (so a duplicated branch arm keeps
 * its side); otherwise the duplicate is added disconnected. The duplicate never
 * copies the source's OUTGOING edges — it is a fresh leaf the user wires up.
 * Returns the new graph plus the inserted node id (unchanged with insertedId ''
 * if the node is the trigger or missing).
 */
export function duplicateNode(
  graph: BuilderGraph,
  nodeId: string
): { graph: BuilderGraph; insertedId: string } {
  const node = nodeById(graph, nodeId)
  if (!node || node.type === WorkflowNodeType.TRIGGER) {
    return { graph, insertedId: '' }
  }

  const position = {
    x: node.position.x + INSERT_X_OFFSET,
    y: node.position.y + VERTICAL_GAP,
  }
  const copy: BuilderNode = {
    ...makeNode(node.type, position, cloneConfig(node.config)),
    description: node.description,
  }

  const nodes = [...graph.nodes, copy]
  const incoming = incomingEdges(graph, nodeId)
  const edges =
    incoming.length === 1
      ? [...graph.edges, makeEdge(incoming[0].from, copy.id, incoming[0].label)]
      : [...graph.edges]

  // A BRANCH is invalid without a true/false path. Scaffold a STOP on the true
  // arm (mirroring appendNodeAfter / insertNodeOnEdge) so a duplicated branch
  // isn't born showing "Branch needs a true/false path"; the user wires the
  // false arm from the branch's explicitly labeled output control.
  if (isRoutingNode(node)) {
    const stop = makeNode(WorkflowNodeType.STOP, {
      x: position.x + INSERT_X_OFFSET,
      y: position.y,
    })
    nodes.push(stop)
    edges.push(makeEdge(copy.id, stop.id, primaryOutputLabel(copy)))
  }

  return {
    graph: {
      nodes,
      edges,
    },
    insertedId: copy.id,
  }
}

// cloneConfig deep-copies a TypedConfig so the duplicate never shares mutable
// references (cc/bcc arrays) with its source. The proto WorkflowCondition on a
// branch is treated as immutable by the builder (replaced wholesale on edit), so
// it is shared by reference, matching configFromProto's handling.
function cloneConfig(config: TypedConfig): TypedConfig {
  switch (config.case) {
    case 'sendTemplatedEmail':
      return {
        case: 'sendTemplatedEmail',
        value: {
          ...config.value,
          cc: [...config.value.cc],
          bcc: [...config.value.bcc],
        },
      }
    case 'wait':
    case 'createTask':
    case 'updateField':
    case 'sendWebhook':
    case 'branch':
      return { case: config.case, value: { ...config.value } } as TypedConfig
    case 'switch':
      return {
        case: 'switch',
        value: {
          ...config.value,
          cases: config.value.cases.map((switchCase) => ({
            ...switchCase,
            values: [...switchCase.values],
          })),
        },
      }
    default:
      return { case: undefined }
  }
}

/**
 * deleteNode removes a node (never the TRIGGER) and rewires the graph. A node
 * with exactly one incoming and one outgoing edge is spliced out: its neighbors
 * are reconnected with a new edge carrying the *incoming* edge's branch label,
 * so a node deleted on a branch arm keeps that arm intact. Any other node just
 * loses its edges. Returns the new graph (unchanged if the node is the trigger
 * or missing).
 */
export function deleteNode(graph: BuilderGraph, nodeId: string): BuilderGraph {
  const node = nodeById(graph, nodeId)
  if (!node || node.type === WorkflowNodeType.TRIGGER) return graph

  const incoming = incomingEdges(graph, nodeId)
  const outgoing = outgoingEdges(graph, nodeId)
  const nodes = graph.nodes.filter((n) => n.id !== nodeId)

  let edges = graph.edges.filter((e) => e.from !== nodeId && e.to !== nodeId)

  // Splice: single in, single out -> reconnect neighbors, preserving the
  // incoming edge's label.
  if (incoming.length === 1 && outgoing.length === 1) {
    const inEdge = incoming[0]
    const outEdge = outgoing[0]
    if (inEdge.from !== outEdge.to) {
      edges = [...edges, makeEdge(inEdge.from, outEdge.to, inEdge.label)]
    }
  }

  return { nodes, edges }
}

export function updateNodeConfig(
  graph: BuilderGraph,
  nodeId: string,
  config: TypedConfig
): BuilderGraph {
  const validSwitchLabels =
    config.case === 'switch'
      ? new Set([...config.value.cases.map((switchCase) => switchCase.id), 'otherwise'])
      : null
  return {
    nodes: graph.nodes.map((node) => (node.id === nodeId ? { ...node, config } : node)),
    edges: validSwitchLabels
      ? graph.edges.filter(
          (edge) => edge.from !== nodeId || validSwitchLabels.has(edge.label ?? '')
        )
      : graph.edges,
  }
}

export function updateNodeDescription(
  graph: BuilderGraph,
  nodeId: string,
  description: string
): BuilderGraph {
  const trimmed = description.trim()
  return {
    nodes: graph.nodes.map((node) =>
      node.id === nodeId ? { ...node, description: trimmed === '' ? undefined : description } : node
    ),
    edges: graph.edges,
  }
}

export function updateNodePosition(
  graph: BuilderGraph,
  nodeId: string,
  position: { x: number; y: number }
): BuilderGraph {
  return {
    nodes: graph.nodes.map((node) => (node.id === nodeId ? { ...node, position } : node)),
    edges: graph.edges,
  }
}

// ---------------------------------------------------------------------------
// defaults & fallback construction
// ---------------------------------------------------------------------------

export const TRIGGER_DEFAULT_POSITION = { x: 240, y: 40 }
const VERTICAL_GAP = 160

export function defaultConfigForType(type: WorkflowNodeType): TypedConfig {
  switch (type) {
    case WorkflowNodeType.WAIT:
      return { case: 'wait', value: { duration: '1d' } }
    case WorkflowNodeType.SEND_TEMPLATED_EMAIL:
      return {
        case: 'sendTemplatedEmail',
        value: {
          toField: '',
          toEmail: '',
          cc: [],
          bcc: [],
          subjectTemplate: '',
          bodyTemplateHtml: '',
          templateName: '',
          templateId: '',
        },
      }
    case WorkflowNodeType.CREATE_TASK:
      return {
        case: 'createTask',
        value: {
          title: '',
          description: '',
          dueInDays: 0,
          assignedToUserId: '',
          assignedToStrategy: '',
        },
      }
    case WorkflowNodeType.UPDATE_FIELD:
      return { case: 'updateField', value: { field: '', value: '' } }
    case WorkflowNodeType.SEND_WEBHOOK:
      return { case: 'sendWebhook', value: { endpointId: '' } }
    case WorkflowNodeType.BRANCH:
      return { case: 'branch', value: { condition: undefined } }
    case WorkflowNodeType.SWITCH:
      return {
        case: 'switch',
        value: {
          field: '',
          cases: [{ id: newSwitchCaseId(), label: 'Case 1', values: [] }],
        },
      }
    case WorkflowNodeType.TRIGGER:
    case WorkflowNodeType.STOP:
    case WorkflowNodeType.UNSPECIFIED:
    default:
      return { case: undefined }
  }
}

export function newNodeId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }
  return `node-${Math.random().toString(36).slice(2)}`
}

export function newEdgeId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }
  return `edge-${Math.random().toString(36).slice(2)}`
}

export function newSwitchCaseId(): string {
  return `case-${newNodeId().toLowerCase()}`
}

export function makeNode(
  type: WorkflowNodeType,
  position: { x: number; y: number },
  config: TypedConfig = defaultConfigForType(type)
): BuilderNode {
  return { id: newNodeId(), type, position, config }
}

export function makeEdge(from: string, to: string, label?: OutputLabel): BuilderEdge {
  return { id: newEdgeId(), from, to, label }
}

function isRoutingNodeType(type: WorkflowNodeType): boolean {
  return type === WorkflowNodeType.BRANCH || type === WorkflowNodeType.SWITCH
}

function isRoutingNode(node: BuilderNode): boolean {
  return isRoutingNodeType(node.type)
}

function primaryOutputLabel(node: BuilderNode): OutputLabel {
  return routingOutputLabels(node)[0] ?? 'otherwise'
}

function fallbackOutputLabel(node: BuilderNode): OutputLabel {
  return node.type === WorkflowNodeType.BRANCH ? 'false' : 'otherwise'
}

/**
 * defaultGraph returns the starting graph for a brand-new workflow: a single
 * TRIGGER node at top center, no edges.
 */
export function defaultGraph(): BuilderGraph {
  return {
    nodes: [makeNode(WorkflowNodeType.TRIGGER, { ...TRIGGER_DEFAULT_POSITION })],
    edges: [],
  }
}

/**
 * graphFromLegacyActions builds a linear graph (trigger -> action chain) from a
 * legacy ordered actions list. Used only as a defensive fallback when a loaded
 * definition has actions but no nodes (older rows the server somehow didn't
 * synthesize). WAIT actions become WAIT nodes; the executable action types
 * map to their node-type counterparts. A legacy inline `delay` prefix on a
 * non-wait action expands into a preceding WAIT node, matching the form-mode
 * behavior. Legacy SEND_EMAIL / SEND_NOTIFICATION actions have no graph node
 * and are skipped — they were never part of the graph model.
 */
export function graphFromLegacyActions(actions: WorkflowAction[]): BuilderGraph {
  const trigger = makeNode(WorkflowNodeType.TRIGGER, {
    ...TRIGGER_DEFAULT_POSITION,
  })
  const nodes: BuilderNode[] = [trigger]
  const edges: BuilderEdge[] = []

  let previousId = trigger.id
  let row = 1

  const ordered = [...actions].sort((a, b) => a.ordering - b.ordering)

  const pushNode = (type: WorkflowNodeType, config: TypedConfig) => {
    const node = makeNode(
      type,
      {
        x: TRIGGER_DEFAULT_POSITION.x,
        y: TRIGGER_DEFAULT_POSITION.y + row * VERTICAL_GAP,
      },
      config
    )
    nodes.push(node)
    edges.push(makeEdge(previousId, node.id))
    previousId = node.id
    row += 1
  }

  for (const action of ordered) {
    const nodeType = nodeTypeForAction(action.actionType)
    if (nodeType === undefined) continue

    if (nodeType !== WorkflowNodeType.WAIT) {
      const legacyDelay = legacyDelayFromActionConfig(action.config)
      if (legacyDelay) {
        pushNode(WorkflowNodeType.WAIT, {
          case: 'wait',
          value: { duration: legacyDelay },
        })
      }
    }

    pushNode(nodeType, configFromAction(action))
  }

  return { nodes, edges }
}

function nodeTypeForAction(actionType: WorkflowActionType): WorkflowNodeType | undefined {
  switch (actionType) {
    case WorkflowActionType.WAIT:
      return WorkflowNodeType.WAIT
    case WorkflowActionType.SEND_TEMPLATED_EMAIL:
      return WorkflowNodeType.SEND_TEMPLATED_EMAIL
    case WorkflowActionType.CREATE_TASK:
      return WorkflowNodeType.CREATE_TASK
    case WorkflowActionType.UPDATE_FIELD:
      return WorkflowNodeType.UPDATE_FIELD
    case WorkflowActionType.SEND_WEBHOOK:
      return WorkflowNodeType.SEND_WEBHOOK
    default:
      return undefined
  }
}

// configFromAction maps a deserialized legacy ActionConfig onto the typed graph
// config. Only the four executable action shapes are reachable here (gated by
// nodeTypeForAction); the wait amount/unit is re-serialized to a duration
// string for the wait node config.
function configFromAction(action: WorkflowAction): TypedConfig {
  const parsed = deserializeActionConfig(action.actionType, action.config)
  switch (parsed.type) {
    case WorkflowActionType.WAIT:
      return {
        case: 'wait',
        value: { duration: serializeWaitDuration(parsed) },
      }
    case WorkflowActionType.SEND_TEMPLATED_EMAIL:
      return {
        case: 'sendTemplatedEmail',
        value: {
          toField: parsed.toField,
          toEmail: parsed.toEmail,
          cc: [...parsed.cc],
          bcc: [...parsed.bcc],
          subjectTemplate: parsed.subjectTemplate,
          bodyTemplateHtml: parsed.bodyTemplateHtml,
          templateName: '',
          templateId: '',
        },
      }
    case WorkflowActionType.CREATE_TASK:
      return {
        case: 'createTask',
        value: {
          title: parsed.title,
          description: parsed.description,
          dueInDays: parsed.dueInDays,
          assignedToUserId: parsed.assignedToUserId,
          assignedToStrategy: '',
        },
      }
    case WorkflowActionType.UPDATE_FIELD:
      return {
        case: 'updateField',
        value: { field: parsed.field, value: parsed.value },
      }
    case WorkflowActionType.SEND_WEBHOOK:
      return { case: 'sendWebhook', value: { endpointId: parsed.endpointId } }
    default:
      return { case: undefined }
  }
}
